import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ImportOrderDetailInformation, UploadImportOrderDetailPdfRequest } from "src/app/domain/models/ImportOrder";
import { ContainerLoad, CreatePalletDataRequest, CreatePalletPriceRequest, CreatePalletsRequest, LoadFilters, MasterLoad, MyLoadRequest, NewPallet, Pallet, PalletInstructiveFormat, PalletLoad, PalletPrice, UpdatePalletsRequest } from "src/app/domain/models/Pallet";
import { Routes } from "src/app/infrastructure/routes";
import { PalletGateway } from "../gateway/PalletGateway";
import { AssignPalletStockRequest, PalletStock } from "src/app/domain/models/PalletStock";
import { PageResult } from "src/app/domain/models/PageResult";
import { StockAlert } from "src/app/domain/models/StockAlert";

@Injectable({providedIn: 'root'})
export class PalletService extends PalletGateway {

  constructor(private _http: HttpClient) {
    super()
  }

  Create(request: CreatePalletsRequest): Observable<Pallet[]> {
    return this._http.post<Pallet[]>(Routes.palletApi + '/Create', request);
  }

  createClosedSale(request: CreatePalletsRequest): Observable<any> {
    return this._http.post<Pallet[]>(Routes.palletApi + '/CreateClosedSale', request);
  }

  getByImportOrderDetailId(importOrderDetailId: string): Observable<ImportOrderDetailInformation> {
    return this._http.get<ImportOrderDetailInformation>(Routes.palletApi + '/GetByImportOrderDetailId', { params: {importOrderDetailId}})
  }

  update(request: CreatePalletsRequest): Observable<Pallet[]> {
    return this._http.put<Pallet[]>(Routes.palletApi + '/Update', request)
  }

  adminVerifyPallets(request: UpdatePalletsRequest): Observable<Pallet[]> {
    return this._http.put<Pallet[]>(Routes.palletApi + '/AdminVerifyPallets', request)
  }

  qcCompanyVerifyPallets(request: UpdatePalletsRequest): Observable<Pallet[]> {
    return this._http.put<Pallet[]>(Routes.palletApi + '/QCCompanyVerifyPallets', request)
  }

  getMasterLoad(year: number): Observable<MasterLoad[]> {
    return this._http.get<MasterLoad[]>(Routes.palletApi + '/GetMasterLoad', { params: {year}})
  }

  uploadQCReviewPdf(request: UploadImportOrderDetailPdfRequest): Observable<any> {
    return this._http.put<Pallet[]>(Routes.importOrderDetailApi + '/UploadQCReviewPdf', this.getUploadFileFormData(request))
  }

  uploadQCContainerConsolidationPdf(request: UploadImportOrderDetailPdfRequest): Observable<any> {
    return this._http.put<Pallet[]>(Routes.importOrderDetailApi + '/UploadQCContainerConsolidationPdf', this.getUploadFileFormData(request))
  }

  private getUploadFileFormData(request: UploadImportOrderDetailPdfRequest) {
    const formData = new FormData()
    formData.append("importOrderDetailId", request.importOrderDetailId.toString())
    formData.append("pdf", request.pdf)
    return formData;
  }

  createPalletPrice(request: CreatePalletPriceRequest): Observable<PalletPrice[]> {
      return this._http.post<PalletPrice[]>(Routes.palletApi + '/CreatePalletPrice', request)
  }

  getPalletPriceConfiguration(importOrderDetailGuid: string): Observable<PalletPrice[]> {
    return this._http.get<PalletPrice[]>(Routes.palletApi + '/GetPriceConfiguration', {params: {importOrderDetailGuid}})
  }

  getPalletPriceConfigurationFromArray(pallets: NewPallet[], withProducer: boolean = false): Observable<PalletPrice[]> {
    return this._http.post<PalletPrice[]>(Routes.palletApi + '/AskConfiguration', {pallets: pallets, withProducer})
  }

  getPalletCaseConfig(importOrderDetailGuid: string): Observable<PalletInstructiveFormat[]> {
    return this._http.get<PalletInstructiveFormat[]>(Routes.palletApi + '/GetCasesConfig', { params: {importOrderDetailGuid}})
  }

  getUserMasterLoad(year: number, isClosedSale:boolean): Observable<MasterLoad[]> {
    return this._http.get<MasterLoad[]>(Routes.palletApi + '/GetMyMasterLoad', { params: {year, isClosedSale}})
  }

  getUserContainerLoad(year: number, isClosedSale: boolean): Observable<ContainerLoad[]> {
    return this._http.get<ContainerLoad[]>(Routes.palletApi + '/GetMyContainerLoad', { params: {year, isClosedSale}})
  }

  getUserPalletLoad(year: number, isClosedSale: boolean): Observable<PalletLoad[]> {
    return this._http.get<PalletLoad[]>(Routes.palletApi + '/GetMyPalletLoad', { params: {year, isClosedSale}})
  }
  cleanPalletConfig(importOrderDetailGuid: string): Observable<any> {

    return this._http.delete<any>(Routes.palletApi + '/CleanPallets', {params:{importOrderDetailGuid}})
  }

  getLatamPalletsConfiguration(request: CreatePalletDataRequest): Observable<Pallet[]>
  {
    return this._http.post<Pallet[]>(Routes.palletApi + '/CreateLatamPalletsConfiguration', request)
  }

  generatePreload(importOrderDetailId: string): Observable<any>
  {
    const headers = new HttpHeaders({
      'Content-Type':  'application/octet-stream'
    })
    return this._http.get(Routes.palletApi + '/GeneratePreload', { params:{importOrderDetailId }, headers: headers, observe: 'response', responseType: 'blob' as 'json'})
  }

  assignPalletStockOrder(palletStockIds: AssignPalletStockRequest,orderDetailGuid: string): Observable<PalletStock[]>
  {
    return this._http.put<any>(Routes.palletApi + '/AssignPalletStockOrder', {orderDetailGuid, palletStockIds, directSaleProgramGuid: null})
  }

  assignPalletStockProgram(palletStockIds: AssignPalletStockRequest,directSaleProgramGuid: string):  Observable<StockAlert[]>
  {
    return this._http.put<StockAlert[]>(Routes.palletApi + '/AssignPalletStockProgram', {orderGuid: null, palletStockIds, directSaleProgramGuid})
  }

  downloadProformaExcel(importOrderDetailGuid: string): Observable<any>
  {
    return this._http.post(Routes.palletApi + '/DownloadProformaExcel',
      {logoUrl: null},
      { params:{importOrderDetailGuid },
      observe: 'response', responseType: 'blob' as 'json'})
  }

  getUserMasterLoadPaged(request: MyLoadRequest): Observable<PageResult<MasterLoad>>
  {
    return this._http.get<any>(Routes.palletApi + '/GetMyMasterLoadPaged', {params: {...request}})
  }

  getUserContainerLoadPaged(request: MyLoadRequest): Observable<PageResult<ContainerLoad>> {
    return this._http.get<PageResult<ContainerLoad>>(Routes.palletApi + '/GetMyContainerLoadPaged', { params: {...request}})
  }

  getLoadsFilter(request: MyLoadRequest): Observable<LoadFilters>
  {
    return this._http.get<any>(Routes.palletApi + '/GetLoadsFilter', {params: {...request}})
  }

  getUserPalletLoadPaged(request: MyLoadRequest): Observable<PageResult<PalletLoad>> {
    return this._http.get<PageResult<PalletLoad>>(Routes.palletApi + '/GetMyPalletLoadPaged', { params: {...request}})
  }

  downloadMyMasterLoad(loadRequest: MyLoadRequest): Observable<any>
  {
    const headers = new HttpHeaders({
      'Content-Type':  'application/octet-stream'
    })
    return this._http.get(Routes.palletApi + '/DownloadMyMasterLoad', { params:{... loadRequest }, headers: headers, observe: 'response', responseType: 'blob' as 'json'})
  }

  downloadMyContainerLoad(loadRequest: MyLoadRequest): Observable<any>
  {
    const headers = new HttpHeaders({
      'Content-Type':  'application/octet-stream'
    })
    return this._http.get(Routes.palletApi + '/DownloadMyContainerLoad', { params:{... loadRequest }, headers: headers, observe: 'response', responseType: 'blob' as 'json'})
  }

  downloadMyPalletLoad(loadRequest: MyLoadRequest): Observable<any>
  {
    const headers = new HttpHeaders({
      'Content-Type':  'application/octet-stream'
    })
    return this._http.get(Routes.palletApi + '/DownloadMyPalletLoad', { params:{... loadRequest }, headers: headers, observe: 'response', responseType: 'blob' as 'json'})
  }
}
